import React from 'react';
import type { ComponentType, SVGProps } from 'react';
import {
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ExclamationCircleIcon,
  EyeIcon,
  HeartIcon,
  InformationCircleIcon,
  MapIcon,
  MegaphoneIcon,
  QrCodeIcon,
  ScaleIcon,
  SparklesIcon,
  TicketIcon,
  TrophyIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

/**
 * Web mirror of packages/domain/src/homeFeedIcons.ts.
 * CRA cannot transpile workspace TypeScript packages directly.
 * Keep in sync with @bilt/domain (mobile uses the package).
 */

export type FeedGlyphId =
  | 'qr'
  | 'trophy'
  | 'heart'
  | 'calendar'
  | 'ticket'
  | 'megaphone'
  | 'people'
  | 'storefront'
  | 'car'
  | 'wrench'
  | 'clipboard'
  | 'scale'
  | 'eye'
  | 'map'
  | 'sparkles'
  | 'alert'
  | 'info';

export type FeedAccentId =
  | 'urgent'
  | 'time_sensitive'
  | 'upcoming'
  | 'passport'
  | 'award'
  | 'favorite'
  | 'build'
  | 'event'
  | 'club'
  | 'shop'
  | 'organizer'
  | 'judge'
  | 'system'
  | 'watched'
  | 'completed'
  | 'discover';

export const FEED_ACCENT_HEX: Record<FeedAccentId, string> = {
  urgent: '#F97316',
  time_sensitive: '#FBBF24',
  upcoming: '#38BDF8',
  passport: '#34D399',
  award: '#FDBA74',
  favorite: '#FB923C',
  build: '#A1A1AA',
  event: '#38BDF8',
  club: '#A78BFA',
  shop: '#2DD4BF',
  organizer: '#FDBA74',
  judge: '#F87171',
  system: '#71717A',
  watched: '#A1A1AA',
  completed: '#34D399',
  discover: '#A78BFA',
};

export type FeedIconInput = {
  type?: string | null;
  variant?: string | null;
  category?: string | null;
  action_required?: boolean | null;
  lane?: string | null;
};

export function resolveFeedGlyph(item: FeedIconInput): FeedGlyphId {
  const t = String(item.type || '').toLowerCase();
  const v = String(item.variant || item.category || '').toLowerCase();

  if (t.includes('passport') || t.includes('qr') || t.includes('scan') || v === 'passport') return 'qr';
  if (t.includes('award') || t.includes('badge')) return 'trophy';
  if (t.includes('fav')) return 'heart';
  if (t.includes('ticket')) return 'ticket';
  if (t === 'product_announcement' || t === 'event_update' || t.includes('announcement')) return 'megaphone';
  if (t === 'organizer_review' || t.includes('organizer')) return 'clipboard';
  if (t === 'judge_assignment' || t.includes('judge')) return 'scale';
  if (t === 'watched_update' || v === 'watched') return 'eye';
  if (t === 'feature_explorer' || t === 'discover_build' || t.includes('explore')) return 'map';
  if (t === 'feature_passport') return 'qr';
  if (t === 'build_progress' || t === 'build_onboarding' || t === 'build_milestone') return 'wrench';
  if (t.includes('club') || v === 'club' || v === 'clubs') return 'people';
  if (t.includes('shop') || v === 'shop' || v === 'shops') return 'storefront';
  if (
    t.includes('event') ||
    t.includes('deadline') ||
    v === 'event' ||
    v === 'upcoming' ||
    v === 'events' ||
    v === 'time_sensitive'
  ) {
    return 'calendar';
  }
  if (t.includes('build') || v === 'build' || v === 'builds' || t.includes('featured')) return 'car';
  if (t.includes('discover') || t.includes('feature')) return 'sparkles';
  if (item.action_required || v === 'urgent') return 'alert';
  return 'info';
}

export function resolveFeedAccent(item: FeedIconInput): FeedAccentId {
  const v = String(item.variant || item.category || '').toLowerCase();
  const t = String(item.type || '').toLowerCase();
  const lane = String(item.lane || '').toLowerCase();

  if (item.action_required || v === 'urgent' || t.includes('payment')) return 'urgent';
  if (t.includes('passport') || t.includes('qr') || t.includes('scan') || v === 'passport') return 'passport';
  if (t.includes('award') || t.includes('badge')) return 'award';
  if (t.includes('fav')) return 'favorite';
  if (t.includes('ticket')) return 'event';
  if (t === 'event_update') return 'event';
  if (t.includes('organizer') || v === 'organizer') return 'organizer';
  if (t.includes('judge') || v === 'judge') return 'judge';
  if (t.includes('club') || v === 'club' || v === 'clubs') return 'club';
  if (t.includes('shop') || v === 'shop' || v === 'shops') return 'shop';
  if (lane === 'discover' || lane === 'featured' || t.includes('discover') || t.includes('feature')) {
    return 'discover';
  }
  if (v === 'time_sensitive' || t.includes('deadline')) return 'time_sensitive';
  if (v === 'upcoming' || t === 'event_upcoming') return 'upcoming';
  if (v === 'event' || v === 'events' || t.includes('event')) return 'event';
  if (v === 'watched' || t.includes('watched')) return 'watched';
  if (v === 'build' || v === 'builds' || t.includes('build') || t.includes('featured')) return 'build';
  if (v === 'completed') return 'completed';
  return 'system';
}

export function splitFeedCountdown(label: string): { primary: string; secondary?: string } {
  const days = label.match(/^(\d+)\s+(days?)$/i);
  if (days) return { primary: days[1], secondary: days[2].toLowerCase() };
  const hours = label.match(/^(\d+)\s+(hours?)$/i);
  if (hours) return { primary: hours[1], secondary: hours[2].toLowerCase() };
  if (/^(1 day)$/i.test(label)) return { primary: '1', secondary: 'day' };
  if (/^(today|soon|tomorrow)$/i.test(label.trim())) {
    return { primary: label.trim() };
  }
  return { primary: label };
}

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Side-profile sports car — optical weight matched to Heroicons outline set. */
export function CarSportIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M2.75 14.25h18.5M4.5 14.25l1.55-4.5A2.25 2.25 0 018.2 8.25h7.6a2.25 2.25 0 012.15 1.5l1.55 4.5M6.75 8.25l.7-1.55A1.75 1.75 0 019.05 5.5h5.9a1.75 1.75 0 011.6 1.2l.7 1.55M6 17a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm12 0a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
      />
    </svg>
  );
}

const GLYPH_ICON: Record<FeedGlyphId, IconComponent> = {
  qr: QrCodeIcon,
  trophy: TrophyIcon,
  heart: HeartIcon,
  calendar: CalendarDaysIcon,
  ticket: TicketIcon,
  megaphone: MegaphoneIcon,
  people: UserGroupIcon,
  storefront: BuildingStorefrontIcon,
  car: CarSportIcon,
  wrench: WrenchScrewdriverIcon,
  clipboard: ClipboardDocumentListIcon,
  scale: ScaleIcon,
  eye: EyeIcon,
  map: MapIcon,
  sparkles: SparklesIcon,
  alert: ExclamationCircleIcon,
  info: InformationCircleIcon,
};

export function feedGlyphComponent(id: FeedGlyphId): IconComponent {
  return GLYPH_ICON[id];
}
